import {
  Box,
  Button,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import ExpandIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { EditSkeleton } from "components/admin/admin.overlays";
import SelectMeal from "components/shared/selectMeal/SelectMeal";
import ResidentInfo from "components/takeOrder/residents/ResidentInfo";
import MultipleOrderSelectModal from "./MultipleOrderSelectModal";
import { useGetResidentsAllQuery } from "store/apis/ResidentsApi";
import { useGetPendingMealsQuery } from "store/apis/MealsApi";
import { useGetOrdersByDateAndMealQuery } from "store/apis/OrdersApi";
import { selectLastMgmtMealSelected, setLastMgmtMealSelected } from "store/slices/orderSlice/OrderSlice";

export default function OrderManagementResidents() {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lastMgmtMeal = useSelector(selectLastMgmtMealSelected);
  const [mealOpen, setMealOpen] = useState(false);
  const [selectedMealId, setSelectedMealId] = useState(lastMgmtMeal?.setupId || 0);
  const [selectedMeal, setSelectedMeal] = useState(lastMgmtMeal?.mealName || '');
  const [shownResidents, setShownResidents] = useState([]);
  const [infoOpen, setInfoOpen] = useState(false);
  const [selectedResidentForInfo, setSelectedResidentForInfo] = useState(0);
  const [multipleOrdersOpen, setMultipleOrdersOpen] = useState(false);
  const [selectedResidentOrders, setSelectedResidentOrders] = useState([]);

  const {
    data: pendingMeals,
    isFetching: fetchingMeals
  } = useGetPendingMealsQuery(true,
    { refetchOnMountOrArgChange: true }
  );

  const {
    data: allResidents,
    isFetching: fetchingResidents
  } = useGetResidentsAllQuery({ refetchOnMountOrArgChange: true });

  const selectedMealObj = pendingMeals?.find(m => m.queuedSetups?.some(s => s.id === selectedMealId));

  const {
    data: mealOrders
  } = useGetOrdersByDateAndMealQuery(
    { diningDate: selectedMealObj?.diningDate, mealId: selectedMealObj?.mealId },
    { refetchOnMountOrArgChange: true, skip: !selectedMealObj?.diningDate || !selectedMealObj?.mealId }
  );

  const navigateToEditOrder = (order) => {
    const mealObj = pendingMeals?.find(m => m.queuedSetups?.some(s => s.id === order.tableSideSetupId));
    navigate(`order/${order.residentId}/${order.tableSideSetupId}?orderId=${order.orderId}`, {
      state: { queuedSetups: mealObj?.queuedSetups ?? [] }
    });
  };

  const isLoading = fetchingMeals || fetchingResidents;

  useEffect(() => {
    if (pendingMeals?.length) {
      const isLastMealCurrent = pendingMeals?.some((meal) => meal?.queuedSetups?.some((s) => s?.id === lastMgmtMeal?.setupId));
      if (isLastMealCurrent) {
        mealSelected(lastMgmtMeal?.setupId, lastMgmtMeal?.mealName);
      } else {
        mealSelected(pendingMeals[0]?.queuedSetups?.[0]?.id, `${pendingMeals[0]?.mealName} (${dayjs(pendingMeals[0]?.diningDate).format('ddd [-] MM/DD/YYYY')})`);
      }
    }
  }, [pendingMeals]);

  useEffect(() => {
    setShownResidents(allResidents ? [...allResidents] : []);
  }, [allResidents]);

  const mealSelected = (setupId, mealName) => {
    setMealOpen(false);
    if (setupId && mealName) {
      setSelectedMealId(setupId);
      setSelectedMeal(mealName);
      dispatch(setLastMgmtMealSelected({ setupId: setupId, mealName: mealName }));
    }
  }

  const filterResidents = (list, searchString) => {
    return list?.filter((resident) => {
      return ((resident?.name?.toLowerCase().indexOf(searchString.toLowerCase()) !== -1) ||
        (resident?.roomNumber?.toLowerCase()?.indexOf(searchString.toLowerCase()) !== -1));
    });
  }

  const searchResidents = (searchString) => {
    let filteredResidents = filterResidents(allResidents, searchString);

    if (filteredResidents?.length === 0 && searchString === '') {
      filteredResidents = [...allResidents];
    }
    setShownResidents([...filteredResidents]);
  }

  const checkResidentModifiers = (resident) => {
    if (resident?.isIsolation) {
      return `${resident.name} ( * Isolation )`;
    } else if (resident.isOutOfBuilding) {
      return `${resident.name} ( * Out of Building )`;
    } else {
      return resident.name;
    }
  }

  const onResidentInfoClick = (resident) => {
    setSelectedResidentForInfo(resident);
    setInfoOpen(true);
  }

  const handleInfoClose = () => {
    setInfoOpen(false);
  }

  // resolves the correct setup for a resident's dining area under the currently selected meal
  const getSetupIdForResident = (resident) => {
    const mealType = selectedMealObj?.mealName?.toLowerCase();
    const residentDiningAreaId = resident?.[`${mealType}DiningAreaId`];
    const setups = selectedMealObj?.queuedSetups ?? [];
    return (
      setups.find(s => s.diningAreaId === residentDiningAreaId)?.id ??
      setups.find(s => s.diningAreaId === null)?.id ??
      selectedMealId
    );
  };

  const getResidentOrders = (resident) => {
    const relatedSetupIds = new Set(selectedMealObj?.queuedSetups?.map(s => s.id) ?? [selectedMealId]);
    return mealOrders?.filter((order) => order.residentId === resident.id && relatedSetupIds.has(order.tableSideSetupId) && !order.isCooksTicket) ?? [];
  }

  const hasOrderTaken = (resident) => {
    return getResidentOrders(resident).length > 0;
  }

  const onResidentOrderClick = (resident) => {
    const residentOrders = getResidentOrders(resident);
    if (residentOrders.length === 1) {
      navigateToEditOrder(residentOrders[0]);
    } else if (residentOrders.length > 1) {
      setSelectedResidentOrders(residentOrders);
      setMultipleOrdersOpen(true);
    }
  }

  const onNewOrderClick = (resident) => {
    const setupId = getSetupIdForResident(resident);
    navigate(`order/${resident.id}/${setupId}`, {
      state: { queuedSetups: selectedMealObj?.queuedSetups ?? [] }
    });
  }

  const handleOrderSelectedFromModal = (order) => {
    setMultipleOrdersOpen(false);
    navigateToEditOrder(order);
  }

  const handleMultipleOrdersClose = () => {
    setMultipleOrdersOpen(false);
  }

  const renderShownResidents = () => {
    return shownResidents?.map((resident) => (
      <ListItem
        key={resident.id}
        style={{ backgroundColor: hasOrderTaken(resident) ? 'lightgray' : 'white', cursor: "pointer" }}
        onClick={() => hasOrderTaken(resident) ? onResidentOrderClick(resident) : onNewOrderClick(resident)}
      >
        <ListItemIcon onClick={(event) => { event.stopPropagation(); onResidentInfoClick(resident); }}> <PersonIcon style={{ transform: "scale(1.5)" }} /> </ListItemIcon>
        <ListItemText primary={<span className="resident-name">{checkResidentModifiers(resident)}</span>} />
        <ListItemSecondaryAction>
          <Typography>{resident?.roomNumber || ''}</Typography>
        </ListItemSecondaryAction>
      </ListItem>
    ));
  }

  return (
    <>
      {isLoading ? (
        <EditSkeleton />
      ) : (
        <div className="paperContent customScrollbar" style={{ backgroundColor: "#FFF", paddingTop: ".5rem" }}>
          <Grid container justifyContent="flex-start" spacing={1} sx={{ marginBottom: ".5rem" }}>
            <Grid size={6} flexDirection="column">
              <Typography
                variant="body1"
                fontWeight="bold"

              >
                SELECT MEAL
              </Typography>
              <Button
                id="meal-btn"
                fullWidth
                variant="contained"
                onClick={() => setMealOpen(true)}
                endIcon={<ExpandIcon />}
              >
                {selectedMeal}
              </Button>
              <SelectMeal
                open={mealOpen}
                pendingMeals={pendingMeals}
                itemSelected={mealSelected}
                title="Select Meal"
                selectedSetupId={selectedMealId}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} flexWrap="wrap" sx={{ margin: ".75rem 0 0" }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                variant="standard"
                type="search"
                onChange={(event) => searchResidents(event.target.value)}
                placeholder="Search by Name, Room Number"
                sx={{
                  "& .MuiInput-root::before": {
                    borderBottom: "2px solid rgba(0, 0, 0, .75)"
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "var(--tealColor)" }} />
                    </InputAdornment>
                  )
                }}
              >
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                <Box sx={{ width: "1.25rem", height: "1.25rem", backgroundColor: "lightgray", border: "1px solid black" }} />
                <Typography variant="body2">Submitted Meal - Edit Meal</Typography>
                <Box sx={{ width: "1.25rem", height: "1.25rem", backgroundColor: "white", border: "1px solid black", marginLeft: "1rem" }} />
                <Typography variant="body2">No Order - Add New</Typography>
              </Box>
            </Grid>
          </Grid>
          <Box
            display="flex"
            flexWrap="wrap"
            justifyContent="flex-end"
          >
            <Typography variant="body1"
              sx={{
                padding: "0 .5rem 0 .5rem",
                backgroundColor: "#9ec6c7",
                color: "#000",
                border: "1px solid rgba(0, 0, 0, .4)",
                borderRadius: ".5rem .5rem 0 0"
              }}
            >
              Room #
            </Typography>
          </Box>
          <List sx={{ paddingY: 0 }}>
            {renderShownResidents()}
          </List>
          <ResidentInfo
            open={infoOpen}
            handleInfoClose={handleInfoClose}
            residentId={selectedResidentForInfo?.id}
            setupId={getSetupIdForResident(selectedResidentForInfo)}
            showPhoto={!!selectedResidentForInfo?.photoOnTrayCard}
          />
          <MultipleOrderSelectModal
            open={multipleOrdersOpen}
            orders={selectedResidentOrders}
            handleClose={handleMultipleOrdersClose}
            handleSelect={handleOrderSelectedFromModal}
          />
        </div>
      )}
    </>
  )
}